import {
	ApolloClient,
	ApolloLink,
	InMemoryCache,
	type NormalizedCacheObject,
	fromPromise,
	split,
} from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { HttpLink } from "@apollo/client/link/http";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { getAccessToken, getGuestWebSocketToken } from "@src/auth/auth-utils";
import { isAbortError, isOfflineNetworkError } from "@src/utils/error-handling";
import { createClient } from "graphql-ws";
import config from "../config";

export type IllumiApolloClient = ApolloClient<NormalizedCacheObject>;

const createHttpLink = () => {
	return new HttpLink({
		uri: config.graphqlUrl,
	});
};

const authLink = setContext(async (_, { headers, skipAuth }) => {
	if (skipAuth) {
		return { headers };
	}

	const token = await getAccessToken();

	if (token) {
		return {
			headers: {
				...headers,
				Authorization: `Bearer ${token}`,
			},
		};
	}

	return { headers };
});

const errorLink = onError(
	({ forward, operation, graphQLErrors, networkError }) => {
		if (graphQLErrors) {
			for (const err of graphQLErrors) {
				switch (err.extensions?.code) {
					case "UNAUTHENTICATED":
						if (operation.getContext().skipAuth) {
							break;
						}
						return fromPromise(getAccessToken()).flatMap((accessToken) => {
							const oldHeaders = operation.getContext().headers || {};
							operation.setContext({
								headers: {
									...oldHeaders,
									Authorization: `Bearer ${accessToken}`,
								},
							});

							return forward(operation);
						});
					case "PAYMENT_REQUIRED":
						// Error propagates to React Query; handleError shows "Plan limit reached"
						break;
				}
			}
		}

		if (graphQLErrors) {
			for (const error of graphQLErrors) {
				const code = error.extensions?.code as string | undefined;
				if (code !== "PAYMENT_REQUIRED") {
					Sentry.captureException(
						new Error(`GraphQL error: ${error.message}`),
						{
							extra: {
								code: error.extensions?.code,
								path: error.path,
								locations: error.locations,
							},
						},
					);
				}
			}
		}

		if (networkError) {
			const isAbort = isAbortError(networkError);
			const isPaymentRequired =
				"statusCode" in networkError && networkError.statusCode === 402;
			const isOffline = isOfflineNetworkError(networkError);

			if (!isAbort && !isPaymentRequired && !isOffline) {
				Sentry.captureException(networkError);
			}
		}
	},
);

export function createWsClient() {
	return new GraphQLWsLink(
		createClient({
			url: config.graphqlWsUrl,
			connectionParams: async () => {
				const token = await getAccessToken();
				if (token) {
					return { Authorization: `Bearer ${token}` };
				}

				const guestToken = await getGuestWebSocketToken();
				if (guestToken) {
					return { guestToken };
				}

				return {};
			},
			// Send a GraphQL-protocol ping every 30 s to keep the Cloud Run /
			// GCLB idle-connection timer from firing.
			keepAlive: 30_000,
			// Retry indefinitely so a Cloud Run redeployment (1001 Going Away)
			// or transient network drop never permanently breaks subscriptions.
			retryAttempts: Number.POSITIVE_INFINITY,
			// First retry is immediate (catches graceful shutdowns); subsequent
			// retries use capped exponential backoff.
			retryWait: async (retries) => {
				const ms =
					retries === 0 ? 0 : Math.min(1000 * 2 ** (retries - 1), 30_000);
				await new Promise((resolve) => setTimeout(resolve, ms));
			},
		}),
	);
}

export function createApolloClient() {
	const wsLink = createWsClient();
	const httpLink = createHttpLink();

	/**
	 * Although Apollo Client can use your GraphQLWsLink to execute all operation types,
	 * in most cases it should continue using HTTP for queries and mutations. This is because
	 * queries and mutations don't require a stateful or long-lasting connection, making HTTP
	 * more efficient and scalable if a WebSocket connection isn't already present.
	 *
	 * https://www.apollographql.com/docs/react/data/subscriptions#3-split-communication-by-operation-recommended
	 */
	const defaultLink = split(
		({ query }) => {
			const definition = getMainDefinition(query);
			return (
				definition.kind === "OperationDefinition" &&
				definition.operation === "subscription"
			);
		},
		wsLink,
		ApolloLink.from([authLink, errorLink, httpLink]),
	);

	return new ApolloClient<NormalizedCacheObject>({
		link: defaultLink,
		cache: new InMemoryCache(),
		defaultOptions: {
			watchQuery: {
				fetchPolicy: "no-cache",
				errorPolicy: "all",
			},
			query: {
				fetchPolicy: "no-cache",
				errorPolicy: "all",
			},
		},
		devtools: {
			enabled: true,
		},
	});
}

let client: ApolloClient<NormalizedCacheObject> | undefined;
export function getApiClient() {
	if (client) {
		return client;
	}

	client = createApolloClient();
	return client;
}
